import React from 'react';
import { motion } from 'framer-motion';
import { Check, Shield, Clock, Zap, Phone, Star, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/seo';
import { PageHero } from '@/components/common/PageHero';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { usePageDesign } from '@/hooks/usePageDesign';

interface MaintenancePlan {
  id: string;
  name: string;
  slug: string;
  tagline: string | null;
  price: number;
  billing_period: string;
  features: string[];
  highlights: string[];
  is_popular: boolean;
  cta_text: string;
  cta_link: string | null;
  sort_order: number;
}

const MaintenancePlansPage: React.FC = () => {
  const { heroStyle, showBreadcrumbs, showTrustBadges, showCtaButtons } = usePageDesign('plans');
  // Fetch business settings
  const { data: businessSettings } = useQuery({
    queryKey: ['business-settings-maintenance'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_settings')
        .select('company_name, phone')
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  const { data: plans, isLoading } = useQuery({
    queryKey: ['maintenance-plans'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('maintenance_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as MaintenancePlan[];
    },
  });

  const companyName = businessSettings?.company_name || 'Our Company';

  return (
    <>
      <SEOHead
        title={`Maintenance Plans | ${companyName}`}
        description="Keep your website running smoothly with our maintenance plans. Choose from Basic, Professional, or Enterprise packages tailored to your needs."
      />

      <div className="min-h-screen">
        <ConfigurableHero
          heroStyle={heroStyle}
          title="Keep Your Website Running Smoothly"
          subtitle="Focus on your business while we handle the technical details. Our maintenance plans ensure your website stays secure, fast, and up-to-date."
          badge="Website Care Plans"
          showBreadcrumbs={showBreadcrumbs}
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Plans' }]}
          showTrustBadges={showTrustBadges}
          showCtaButtons={showCtaButtons}
        />

        {/* Plans Grid */}
        <section className="py-12 lg:py-20">
          <div className="container mx-auto px-4">
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-[600px] rounded-2xl" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {plans?.map((plan, index) => (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.15 }}
                    className="relative"
                  >
                    <Card
                      className={`relative h-full flex flex-col transition-all duration-300 hover:shadow-xl ${
                        plan.is_popular
                          ? 'ring-2 ring-primary shadow-lg scale-[1.02] z-10'
                          : 'hover:scale-[1.01]'
                      }`}
                    >
                      {/* Popular Badge */}
                      {plan.is_popular && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                          <Badge className="bg-primary text-primary-foreground px-4 py-1 text-sm shadow-lg">
                            <Star className="h-3.5 w-3.5 mr-1.5" />
                            Most Popular
                          </Badge>
                        </div>
                      )}

                      <CardHeader className={`text-center pb-4 ${plan.is_popular ? 'pt-8' : 'pt-6'}`}>
                        <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
                        {plan.tagline && (
                          <CardDescription className="text-base mt-2">
                            {plan.tagline}
                          </CardDescription>
                        )}
                        
                        {/* Price */}
                        <div className="mt-6">
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-5xl font-bold text-foreground">
                              ${plan.price}
                            </span>
                            <span className="text-muted-foreground">/{plan.billing_period}</span>
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent className="flex-1 flex flex-col">
                        {/* Highlights */}
                        <div className="flex flex-wrap gap-2 justify-center mb-6">
                          {plan.highlights?.map((highlight, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {highlight}
                            </Badge>
                          ))}
                        </div>

                        {/* Features List */}
                        <ul className="space-y-3 flex-1">
                          {plan.features?.map((feature, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="h-3 w-3 text-primary" />
                              </div>
                              <span className="text-sm text-muted-foreground">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        {/* CTA Button */}
                        <div className="mt-8">
                          <Button
                            asChild
                            className={`w-full h-12 text-base ${
                              plan.is_popular ? '' : 'bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground'
                            }`}
                            variant={plan.is_popular ? 'default' : 'ghost'}
                          >
                            <Link to={plan.cta_link || '/contact'}>
                              {plan.cta_text}
                              <ArrowRight className="h-4 w-4 ml-2" />
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
                Why Choose a Maintenance Plan?
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                A well-maintained website means better performance, security, and peace of mind
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {[
                {
                  icon: Shield,
                  title: 'Security First',
                  description: 'Regular updates and monitoring keep your site protected from threats',
                },
                {
                  icon: Zap,
                  title: 'Peak Performance',
                  description: 'Optimizations ensure your site loads fast and runs smoothly',
                },
                {
                  icon: Clock,
                  title: 'Save Time',
                  description: 'Focus on your business while we handle the technical work',
                },
                {
                  icon: Phone,
                  title: 'Expert Support',
                  description: 'Get help when you need it from our dedicated team',
                },
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center p-6"
                >
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4 max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
                Common Questions
              </h2>
            </motion.div>

            <div className="space-y-4">
              {[
                {
                  q: 'What happens if I need more hours than my plan includes?',
                  a: 'Additional hours can be purchased at $100/hour, or you can upgrade to a higher plan anytime.',
                },
                {
                  q: 'Can I cancel or change my plan?',
                  a: 'Yes! You can upgrade, downgrade, or cancel your plan at any time. Changes take effect on your next billing cycle.',
                },
                {
                  q: 'What counts as an "update"?',
                  a: 'Updates include content changes, minor design tweaks, plugin updates, and small feature additions. Major redesigns or new features may require additional hours.',
                },
                {
                  q: 'How quickly will you respond to issues?',
                  a: 'Response times vary by plan. Basic is 48 hours, Professional is 24 hours, and Enterprise gets priority same-day response.',
                },
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="surface p-6"
                >
                  <h3 className="font-semibold text-foreground mb-2">{faq.q}</h3>
                  <p className="text-muted-foreground text-sm">{faq.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-24 bg-primary/5">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl mx-auto"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Have questions or need a custom plan? We're here to help.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="h-12 px-8">
                  <Link to="/contact">
                    Contact Us
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-12 px-8">
                  <a href={`tel:${businessSettings?.phone || ''}`}>
                    <Phone className="h-4 w-4 mr-2" />
                    Call Now
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default MaintenancePlansPage;
